import { useEffect, useState } from 'react';
import {
  listShopifyShops,
  disconnectShopifyShop,
  shopifyStartUrl,
  type ShopifyShop,
} from '../../api/client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { X } from 'lucide-react';

interface ShopifyConnectModalProps {
  onClose: () => void;
  // Reports the current number of connected stores so the card can flip its
  // connected/disconnected state (multiple stores can hang off one project).
  onShopsChanged: (count: number) => void;
}

// Manage Shopify stores: list connected stores (with per-store disconnect) and add
// another. Installs MUST be initiated on a Shopify surface (App Store requirement
// 2.3.1), so we never ask for a *.myshopify.com domain: Install goes to the
// backend's /api/shopify/start, which redirects to this environment's configured
// Shopify install link (the App Store listing in production). After OAuth the
// backend parks the store and returns the merchant to /shopify/claim, which claims
// it onto the account.
export default function ShopifyConnectModal({ onClose, onShopsChanged }: ShopifyConnectModalProps) {
  const [shops, setShops] = useState<ShopifyShop[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const s = await listShopifyShops();
      setShops(s);
      onShopsChanged(s.length);
    } catch {
      /* ignore — leave the current list */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInstall = () => {
    // Tell the backend dispatcher which product frontend to return to after the
    // Shopify-initiated install (the OAuth carries no origin). Cookie on .vib3.tech
    // so the backend (audit-*.vib3.tech) reads it; host-only on localhost.
    const onVib3 = window.location.hostname.endsWith('vib3.tech');
    const secure = window.location.protocol === 'https:' ? '; secure' : '';
    const domain = onVib3 ? '; domain=.vib3.tech' : '';
    document.cookie =
      `shopify_return=${window.location.origin}; path=/; max-age=600; samesite=lax` +
      domain +
      secure;
    // This product has no projects, so it carries no project context — but the
    // carriers live on `.vib3.tech`, shared with the product that does. Clearing
    // them is what stops an abandoned install started over there from attaching
    // ITS project id to the install starting here.
    for (const stale of ['shopify_project', 'shopify_org']) {
      document.cookie = `${stale}=; path=/; max-age=0; samesite=lax` + domain + secure;
    }
    // Start the install through the backend's redirect, which reads the install
    // link configured for this environment on every request; after OAuth the
    // backend returns the merchant to this frontend's /shopify/claim.
    window.location.href = shopifyStartUrl();
  };

  const handleDisconnect = async (domain: string) => {
    try {
      await disconnectShopifyShop(domain);
    } catch {
      /* ignore */
    }
    await refresh();
  };

  return (
    <Dialog open onOpenChange={(open: boolean) => { if (!open) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Shopify stores</DialogTitle>
          <DialogDescription>
            Connect one or more stores — each is authorized separately.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {!loading && shops.length > 0 && (
            <div className="space-y-2">
              {shops.map((s) => (
                <div
                  key={s.shop_domain}
                  className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2"
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-foreground">
                      {s.shop_name || s.shop_domain}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {s.shop_domain}
                      {s.status && s.status !== 'active' ? ` · ${s.status}` : ''}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8 shrink-0 cursor-pointer text-muted-foreground hover:text-destructive"
                    onClick={() => handleDisconnect(s.shop_domain)}
                    title="Disconnect this store"
                  >
                    <X className="size-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-1.5">
            <p className="text-sm font-medium text-foreground">
              {shops.length > 0 ? 'Add another store' : 'Connect a store'}
            </p>
            <p className="text-sm text-muted-foreground">
              Install the app from the Shopify App Store. Shopify will ask you to
              choose your store, then send you back here to finish connecting.
            </p>
            <Button className="font-semibold tracking-tight" onClick={handleInstall}>
              Install from the Shopify App Store
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
